package fr.frux.navigation

import fr.frux.core.designsystem.layout.FruxLayoutMode

/**
 * Où un écran se pose sur grand écran (spec 2026-09-10 webApp grand écran, §3). Sans effet en
 * mode compact : le squelette téléphone ne lit pas la zone.
 */
enum class LayoutZone {
    /** Explorer nu : carte pleine zone à droite du rail, chrome flottant, aucune colonne (spec §5). */
    MapChromeZone,

    /** Colonne de 400 dp posée sur la carte persistante, carte interactive à côté. */
    MapZone,

    /** Fond opaque sur toute la zone, contenu centré dans une colonne bornée. */
    FullZone,

    /** Sans rail : accueil et connexion (écran scindé au lot 3). */
    EntryZone,
}

/** `when` exhaustif volontairement sans `else` : une route nouvelle ne compile pas sans zone. */
fun Route.layoutZone(): LayoutZone =
    when (this) {
        is Route.Map -> LayoutZone.MapChromeZone

        is Route.TreeDetail,
        is Route.TreeList,
        is Route.Harvests,
        is Route.HarvestDetail,
        is Route.CreateHarvest,
        is Route.Favorites,
        is Route.MyTrees,
        is Route.AddTree,
        is Route.MoveTree,
        is Route.UserProfile,
        is Route.SiteDetail,
        is Route.CreateSite,
        -> LayoutZone.MapZone

        is Route.Profile,
        is Route.Settings,
        is Route.Notifications,
        is Route.NotificationPreferences,
        is Route.Feedback,
        is Route.Premium,
        is Route.Showcase,
        is Route.BlockedUsers,
        -> LayoutZone.FullZone

        is Route.Welcome,
        is Route.Auth,
        -> LayoutZone.EntryZone
    }

/**
 * La carte persistante reçoit les gestes sur Explorer, et en grand écran à côté de toute colonne
 * (spec §3 et §4, carte interactive à côté d'une colonne). Pendant la visée d'un arbre ([aimActive]),
 * elle les reçoit aussi sur téléphone, où l'assistant et le déplacement sont des feuilles basses et
 * non une colonne.
 * Partout ailleurs un scrim les consomme (cf. MapSurface).
 */
fun Route.isMapInteractive(
    mode: FruxLayoutMode,
    aimActive: Boolean = false,
): Boolean =
    this is Route.Map ||
        (keepsAim() && aimActive) ||
        (mode == FruxLayoutMode.Expanded && layoutZone() == LayoutZone.MapZone)

/**
 * Routes qui composent avec la carte en visée : l'assistant d'ajout et le déplacement d'un arbre.
 * Partout ailleurs, App arrête la visée (garde anti-fuite).
 */
fun Route.keepsAim(): Boolean = this is Route.AddTree || this is Route.MoveTree

/** Clé de métadonnée Nav3 portant la zone d'une entrée, lue par le décorateur de colonne (App). */
const val ZONE_METADATA_KEY = "frux.layoutZone"

/**
 * À poser sur chaque entrée de `MapZone` dans l'entry provider : la clé de contenu d'une entrée
 * Nav3 n'est pas la route, la zone doit voyager avec l'entrée pour que l'écran porte sa colonne.
 */
val mapPanelMetadata: Map<String, Any> = mapOf(ZONE_METADATA_KEY to LayoutZone.MapZone)

/** Clé de métadonnée Nav3 d'un écran de pleine zone qui occupe tout l'hôte au lieu de la colonne. */
const val WIDE_METADATA_KEY = "frux.wideFullZone"

/**
 * À poser sur chaque entrée de `FullZone` : l'écran porte sa colonne de 640 dp, pour que l'hôte
 * garde la même largeur d'un écran de la zone à l'autre (#443).
 */
val fullZoneMetadata: Map<String, Any> = mapOf(ZONE_METADATA_KEY to LayoutZone.FullZone)

/** Entrée de `FullZone` qui prend toute la largeur de l'hôte (Profil en deux colonnes, #443). */
val wideFullZoneMetadata: Map<String, Any> = fullZoneMetadata + (WIDE_METADATA_KEY to true)
